/**
 * Registration Details Constants
 * Centralized constants for registration status options and delivery times
 */
import manageOrdersLabels from '../../../../../../scripts/i18n/output/manage-orders.json';
/**
 * Auto-hide duration for success messages (in milliseconds)
 */
export const SUCCESS_MESSAGE_DURATION_MS = 3000;

export const REGISTRATION_STATUS_PRE_REGISTERED = 'Pre-Registered';

/**
 * Fallback for 'error.save' label, used only while labels haven't loaded yet (sourced from manage-orders.json)
 */
export const DEFAULT_SAVE_ERROR_MESSAGE = manageOrdersLabels.locales['en-us'][
  'error.save'
] as string;

/**
 * Map of known English API error messages to the Contentful label key holding
 * their translation (populated via scripts/i18n/output/manage-orders.json).
 * `labels` is already fetched for the active locale/language, so we just swap
 * the raw API string for the matching translated label.
 */
const VEHICLE_ETA_UNAVAILABLE_ERROR = manageOrdersLabels.locales['en-us'][
  'error.vehicleEtaUnavailable'
] as string;

export const API_ERROR_LABEL_KEYS: Record<string, string> = {
  [VEHICLE_ETA_UNAVAILABLE_ERROR]: 'error.vehicleEtaUnavailable',
};

/**
 * Code-level fallback translations, used until the matching Contentful label key is published.
 * Values are read directly from manage-orders.json (the same source uploaded to Contentful)
 * instead of being duplicated as string literals here.
 */
export const API_ERROR_FALLBACK_TRANSLATIONS: Record<string, Record<string, string>> = {
  [VEHICLE_ETA_UNAVAILABLE_ERROR]: {
    en: VEHICLE_ETA_UNAVAILABLE_ERROR,
    nl: manageOrdersLabels.locales['nl-nl']['error.vehicleEtaUnavailable'],
  },
};

/**
 * Translate a raw API error message, preferring the Contentful label for the active
 * locale and falling back to a code-level translation if that label isn't published yet
 * @param errorMessage - English error message from API
 * @param labels - i18n labels already fetched for the active locale/language
 * @param language - Label-language override from the store; null means "use the locale's native language"
 * @param locale - Active region locale (e.g. 'nl-nl'), used to resolve the language when `language` is null
 * @returns Translated error message or original message if no mapping/translation exists
 */
export const getLocalizedErrorMessage = (
  errorMessage: string,
  labels?: Record<string, string>,
  language?: string | null,
  locale?: string
): string => {
  const labelKey = API_ERROR_LABEL_KEYS[errorMessage];
  if (labelKey && labels?.[labelKey]) {
    return labels[labelKey];
  }

  const fallbackTranslations = API_ERROR_FALLBACK_TRANSLATIONS[errorMessage];
  if (!fallbackTranslations) {
    return errorMessage;
  }

  // null language means "use the locale's native language" (e.g. 'nl' for 'nl-nl')
  const languageCode = (language ?? locale?.split('-')[0] ?? 'en').toLowerCase();
  return fallbackTranslations[languageCode] || fallbackTranslations.en || errorMessage;
};

/**
 * Registration status options for the dropdown field
 * @param labels - i18n labels from Contentful
 * @returns Array of registration status options with label and value properties for dropdown selection
 */
export const getRegistrationStatusOptions = (labels?: Record<string, string>) => [
  { label: labels?.['const.registrationStatus.none'] ?? '--None--', value: '' },
  {
    label: labels?.['const.registrationStatus.preRegistered'] ?? 'Pre-Registered',
    value: REGISTRATION_STATUS_PRE_REGISTERED,
  },
  { label: labels?.['const.registrationStatus.created'] ?? 'Created', value: 'Created' },
  {
    label: labels?.['const.registrationStatus.sentToCustomer'] ?? 'Sent to Customer',
    value: 'Sent to Customer',
  },
  { label: labels?.['const.registrationStatus.hold'] ?? 'Hold', value: 'Hold' },
  {
    label: labels?.['const.registrationStatus.pendingDocuments'] ?? 'Pending Documents',
    value: 'Pending Documents',
  },
  {
    label: labels?.['const.registrationStatus.documentsReceived'] ?? 'Documents Received',
    value: 'Documents Received',
  },
  {
    label:
      labels?.['const.registrationStatus.sentToRegistrationOffice'] ??
      'Sent to Registration Office',
    value: 'Sent to Registration Office',
  },
  {
    label: labels?.['const.registrationStatus.deregistered'] ?? 'Deregistered',
    value: 'Deregistered',
  },
  { label: labels?.['const.registrationStatus.registered'] ?? 'Registered', value: 'Registered' },
  { label: labels?.['const.registrationStatus.finalized'] ?? 'Finalized', value: 'Finalized' },
];

/**
 * Scheduled delivery time options (H:mm values matching backend format, 12h display labels)
 * @param labels - i18n labels from Contentful
 * @returns Array of delivery time options with label and value properties for dropdown selection
 */
export const getDeliveryTimeOptions = (labels?: Record<string, string>) => [
  { label: labels?.['const.deliveryTime.9am'] ?? '9:00 AM', value: '9:00' },
  { label: labels?.['const.deliveryTime.10am'] ?? '10:00 AM', value: '10:00' },
  { label: labels?.['const.deliveryTime.11am'] ?? '11:00 AM', value: '11:00' },
  { label: labels?.['const.deliveryTime.12pm'] ?? '12:00 PM', value: '12:00' },
  { label: labels?.['const.deliveryTime.1pm'] ?? '1:00 PM', value: '13:00' },
  { label: labels?.['const.deliveryTime.2pm'] ?? '2:00 PM', value: '14:00' },
  { label: labels?.['const.deliveryTime.3pm'] ?? '3:00 PM', value: '15:00' },
  { label: labels?.['const.deliveryTime.4pm'] ?? '4:00 PM', value: '16:00' },
  { label: labels?.['const.deliveryTime.5pm'] ?? '5:00 PM', value: '17:00' },
  { label: labels?.['const.deliveryTime.6pm'] ?? '6:00 PM', value: '18:00' },
  { label: labels?.['const.deliveryTime.7pm'] ?? '7:00 PM', value: '19:00' },
  { label: labels?.['const.deliveryTime.8pm'] ?? '8:00 PM', value: '20:00' },
  { label: labels?.['const.deliveryTime.9pm'] ?? '9:00 PM', value: '21:00' },
];
